import { getParsedCookie } from '../helpers/cookie';
import { CookieConsentStoreCookie } from './useCookieConsentStore.types';
import {
  convertGTMConsentValues,
  CONSENT_COOKIE_NAME,
} from './useCookieConsentStore.utils';

const DEFAULT_US_CONSENT_SETTINGS = {
  ...convertGTMConsentValues({
    ad_personalization: true,
    ad_storage: true,
    ad_user_data: true,
    analytics_storage: true,
    functionality_storage: true,
  }),
  region: ['US'],
};

const DEFAULT_CONSENT_SETTINGS = convertGTMConsentValues({
  ad_personalization: false,
  ad_storage: false,
  ad_user_data: false,
  analytics_storage: false,
  functionality_storage: true,
});

/**
 * Generates a Google Tag Manager (GTM) script string configured with the specified GTM ID.
 *
 * @param gtmId - The Google Tag Manager ID (e.g., "GTM-XXXXXX") to be embedded in the script.
 * @returns A string containing the complete GTM setup script.
 *
 * @remarks
 * Please ensure this util runs on the client side as it relies on the `window` object (to get consent settings).
 *
 * @example
 * import Script from 'next/script';
 *
 * const RootLayout = () => (
 *   <>
 *     <Script
 *       id="gtag"
 *       strategy="afterInteractive"
 *       dangerouslySetInnerHTML={{ __html: getGtmScript('GTM-XXXXXX') }}
 *     />
 *   </>
 * );
 */
export const getGtmScript = (gtmId: string) => {
  const consentCookie =
    getParsedCookie<CookieConsentStoreCookie>(CONSENT_COOKIE_NAME);

  const consentSettingsScript = consentCookie?.state
    ? `gtag('consent', 'default', ${JSON.stringify(convertGTMConsentValues(consentCookie.state))});`
    : `
      gtag('consent', 'default', ${JSON.stringify(DEFAULT_US_CONSENT_SETTINGS)});
      gtag('consent', 'default', ${JSON.stringify(DEFAULT_CONSENT_SETTINGS)});
    `;

  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}

    ${consentSettingsScript}

    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');`;
};
